import { FunctionComponent, useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/layout';
import {Box, Button, Grid, Typography, List, ListItem, ListItemText, Paper, ClickAwayListener } from '@mui/material';
import { StyledTextField } from '../../components/ui/formInput';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { useGetInventoryAutocomplete } from '../../hooks/api/user';
//import StorageService from '../../services/storageService';
//import RecentInventorySearch from '../../components/common/recent-inventory-search';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  partNumber: ValidateField;
};


const InventorySearch: FunctionComponent = () => {
  const navigate = useNavigate();
  const [partNumber, setPartNumber] = useState<string>('');
  const [searchPartNumber, setSearchPartNumber] = useState<string>('');
  const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
  const [debouncedPrefix, setDebouncedPrefix] = useState<string>('');
  const autocompleteRef = useRef<HTMLDivElement>(null);
  
  const formValidation: ValidateFieldsState = {
    partNumber: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  // Autocomplete query
  const { data: autocompleteData, isLoading: isAutocompleteLoading, error: autocompleteError } = useGetInventoryAutocomplete(debouncedPrefix);
  const suggestions = autocompleteData?.partNumbers || [];



  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!partNumber?.trim()) {
      errors.partNumber = { invalid: true, error: 'Please enter a part number.' };
      valid = false;
    } else {
      errors.partNumber = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  };

  const handleAction = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    const trimmed = partNumber.trim();
    setSearchPartNumber(trimmed);

    // StorageService.setRecentSearchPartNumber(partNumber.trim());
    navigate(`/inventory/inventory-search-result?partNo=${encodeURIComponent(trimmed)}`);
  }

  const queryClient = useQueryClient();

  // Debounce effect for autocomplete
  useEffect(() => {
    const timer = setTimeout(() => {
      if (partNumber.trim().length >= 2) {
        setDebouncedPrefix(partNumber.trim());
        setShowAutocomplete(true);
      } else {
        setShowAutocomplete(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [partNumber]);

  // Handle suggestion selection - auto-search
  const handleSuggestionClick = (suggestion: string) => {
    setPartNumber(suggestion);
    setShowAutocomplete(false);
    // Auto-trigger search
    setTimeout(() => {
      navigate(`/inventory/inventory-search-result?partNo=${encodeURIComponent(suggestion)}`);
    }, 100); // Small delay to ensure state is updated
  };

  // Handle input change
  const handlePartNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPartNumber(value);
    setValidateFields(formValidation); // Clear validation errors
  };

  // Handle click away from autocomplete
  const handleClickAway = () => {
    setShowAutocomplete(false);
  };

  const handleBack = () => {
    queryClient.clear();
    navigate('/inventory');
  };


  return (
    <>
      <DashboardLayout titleIcon={<LockOutlinedIcon />} title={"Inventory"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={''} breadcrumbs={' Inventory Search'} activeMenu={'inventory'}>
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
          <Grid>
            <Grid>
              <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
                Barksdale Part Number :{searchPartNumber}
                {/* {inventoryList.find((item) => item.partNo)?.partNo} */}
              </Typography>

            </Grid>
            <form onSubmit={handleAction}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                <Box sx={{ position: 'relative', flex: 1 }}>
                  <StyledTextField
                    label="Enter Part Number"
                    value={partNumber}
                    onChange={handlePartNumberChange}
                    error={validateFields.partNumber.invalid}
                    helperText={validateFields.partNumber.error}
                  />
                  {showAutocomplete && (
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <Paper
                        ref={autocompleteRef}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          maxHeight: 200,
                          overflow: 'auto',
                          boxShadow: 3,
                          border: '1px solid #ddd',
                          mt: 0.5
                        }}
                      >
                        <List dense>
                          {isAutocompleteLoading ? (
                            <ListItem>
                              <ListItemText primary="Loading..." />
                            </ListItem>
                          ) : suggestions.length > 0 ? (
                            suggestions.map((suggestion: string, index: number) => (
                              <ListItem
                                key={index}
                                component="div"
                                onClick={() => handleSuggestionClick(suggestion)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                  }
                                }}
                              >
                                <ListItemText primary={suggestion} />
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText primary="No suggestions found" />
                            </ListItem>
                          )}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  )}
                </Box>
                <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} type="submit">
                  SEARCH
                </Button>

            </Box>
            </form>
            
          </Grid>
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Box>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#c3183c', padding: '7px 25px !important' }}
                  onClick={handleBack}
                >
                  BACK
                </Button>
              </Box>
            </Box>

          </Box>
  
      </DashboardLayout>
    </>
  )

};

export default InventorySearch;